import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import HoverTooltip from './HoverTooltip';
import UniversalMediaRenderer from './UniversalMediaRenderer';
import AiToolsButton from './AiToolsButton';

const WhisperCard = ({ entry, user, isSelf, onPin, onSpotlight, onAiAction, showConfirmation, t, onRemix, onBookmark, onShowInteractions, onComment }) => {
    const { LucideIcons, appFunctions, setMessage, userId, currentUserProfile } = useAppContext();
    const { i18n } = useTranslation();
    const canSpotlight = (user?.influenceScore || 0) >= 1000;
    const [showFullContent, setShowFullContent] = useState(false);
    const [translatedContent, setTranslatedContent] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isAiMenuOpen, setIsAiMenuOpen] = useState(false);

    // State for reactions
    const [likesCount, setLikesCount] = useState(entry.likesCount || 0);
    const [dislikesCount, setDislikesCount] = useState(entry.dislikesCount || 0);
    const [amplifiesCount, setAmplifiesCount] = useState(entry.echoesInvested || 0);
    const [userReaction, setUserReaction] = useState(() => {
        if (entry.likes?.includes(userId)) return 'like';
        if (entry.dislikes?.includes(userId)) return 'dislike';
        return null;
    });

    const handleDelete = async () => {
        try {
            const deleteContent = httpsCallable(appFunctions, 'deleteContent');
            await deleteContent({ contentId: entry.id, contentType: 'whisper' });
            setMessage(t('whisperCard.deleteSuccess'));
            // Optionally, you can call a prop to notify the parent to remove the card from the UI
            // e.g., onDeletedFromState(entry.id)
        } catch (error) {
            console.error("Error deleting whisper:", error);
            setMessage(t('whisperCard.deleteError', { message: error.message }));
        }
    };

    const handleReaction = useCallback(async (reaction) => {
        if (!userId) { setMessage(t('signInRequired')); return; }
        
        const originalReaction = userReaction;
        const originalLikes = likesCount;
        const originalDislikes = dislikesCount;

        // Optimistic UI Update
        setUserReaction(prevReaction => {
            const newReaction = prevReaction === reaction ? null : reaction;
            
            if (prevReaction === 'like') setLikesCount(c => c - 1);
            else if (newReaction === 'like') setLikesCount(c => c + 1);

            if (prevReaction === 'dislike') setDislikesCount(c => c - 1);
            else if (newReaction === 'dislike') setDislikesCount(c => c + 1);

            return newReaction;
        });

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: entry.id, authorId: entry.authorId, reaction, contentType: 'whisper' });
        } catch (error) {
            // Revert UI on failure
            setUserReaction(originalReaction);
            setLikesCount(originalLikes);
            setDislikesCount(originalDislikes);
            console.error(`Error syncing '${reaction}' reaction:`, error);
            setMessage(error.message);
        }
    }, [userId, appFunctions, setMessage, t, entry.id, entry.authorId, userReaction, likesCount, dislikesCount]);

    const handleAmplify = useCallback(async () => {
        if (!userId) { setMessage(t('signInRequired')); return; }
        if (isSelf) return; // Should be disabled in UI, but extra guard here.
        const amountToAmplify = 10;

        // Optimistic UI Update
        setAmplifiesCount(c => c + amountToAmplify);

        try {
            const amplifyWhisper = httpsCallable(appFunctions, 'amplifyWhisper');
            await amplifyWhisper({ whisperId: entry.id, amount: amountToAmplify });
        } catch (error) {
            setAmplifiesCount(c => c - amountToAmplify); // Revert on failure
            console.error("Error amplifying whisper:", error);
            setMessage(t('anonymousFeed.amplifyError'));
        }
    }, [userId, appFunctions, setMessage, t, entry.id, isSelf]);

    const handleTranslate = async () => {
        setIsTranslating(true);
        try {
            const translateWhisper = httpsCallable(appFunctions, 'translateWhisper');
            const result = await translateWhisper({ text: entry.content, targetLanguage: i18n.language });
            setTranslatedContent(result.data.translatedText);
        } catch (error) {
            setMessage(t('whisperCard.translationFailed', { message: error.message }));
        } finally {
            setIsTranslating(false);
        }
    };

    const handleShare = () => {
        const url = `${window.location.origin}?page=viewingProfile&userId=${entry.authorId}&scrollToEntry=${entry.id}`;
        navigator.clipboard.writeText(url)
            .then(() => setMessage(t('whisperCard.shareSuccess')))
            .catch(() => setMessage(t('whisperCard.shareError')));
    };

    return (
        <div className={`scroll-snap-align-start w-full bg-gray-800/50 rounded-xl shadow-lg my-4 ${(entry.spotlighted || entry.spotlight) ? 'spotlighted-whisper' : ''}`} style={{ position: 'relative', zIndex: isAiMenuOpen ? 20 : 'auto' }}>
            <div className="p-5">
                {user && (
                    <div className="flex items-start mb-4">
                        <img src={user.photoURL || `https://placehold.co/48x48/AEC6CF/FFFFFF?text=${user.displayName[0]}`} alt={user.displayName} className="w-12 h-12 rounded-full mr-4 border-2 border-gray-700" />
                        <div className="flex-grow flex justify-between items-start">
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="font-bold text-white text-lg">{user.displayName}</p>
                                    {!isSelf && user && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                showConfirmation({
                                                    message: t('userProfile.actions.addToInnerCircleConfirmation') || "Add this user to your Inner Circle?",
                                                    onConfirm: async () => {
                                                        try {
                                                            const addInnerCircleMember = httpsCallable(appFunctions, 'addInnerCircleMember');
                                                            await addInnerCircleMember({ targetUserId: user.id });
                                                            setMessage(t('userProfile.success.addedToInnerCircle') || "Added to Inner Circle");
                                                        } catch (error) {
                                                            setMessage(t('userProfile.errors.addToInnerCircle', { message: error.message }) || `Error: ${error.message}`);
                                                        }
                                                    }
                                                });
                                            }}
                                            className="p-1 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10 rounded-full transition-colors"
                                            title={t('userProfile.actions.addToInnerCircle') || "Add to Inner Circle"}
                                        >
                                            <LucideIcons.Heart size={16} />
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400">
                                    {entry.timestamp?.toDate().toLocaleString()}
                                    {entry.editedAt && (
                                        <HoverTooltip text={entry.editedAt?.toDate().toLocaleString()}>
                                            <span className="ml-1 text-gray-500">({t('edited')})</span>
                                        </HoverTooltip>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <p className="text-gray-200 mb-4 text-lg leading-relaxed">
                    {entry.content.length > 280 && !showFullContent
                        ? `${entry.content.substring(0, 280)}...`
                        : entry.content}
                    {entry.content.length > 280 && (
                        <button onClick={() => setShowFullContent(!showFullContent)} className="text-blue-400 hover:underline ml-1">
                            {showFullContent ? t('seeLess') : t('seeMore')}
                        </button>
                    )}
                </p>
                {translatedContent && (
                    <p className="text-gray-300 mb-4 text-sm italic border-l-2 border-blue-400 pl-2">
                        {t('translated')}: {translatedContent}
                    </p>
                )}
                <UniversalMediaRenderer entry={entry} />
                <div className="mt-4 flex justify-between items-center text-sm text-gray-400">
                    <div className="flex items-center gap-4">
                        <span>{likesCount} {t('likes')}</span>
                        <span>{dislikesCount} {t('dislikes')}</span>
                        <span>{amplifiesCount} {t('amplifies')}</span>
                    </div>
                    {entry.language && entry.language !== i18n.language && (
                        <button onClick={handleTranslate} disabled={isTranslating} className="flex items-center gap-1 text-blue-400 hover:underline">
                            {isTranslating ? <div className="action-spinner" /> : <LucideIcons.Languages size={16} />}
                            <span>{t('translate')}</span>
                        </button>
                    )}
                </div>
            </div>

<div className="bg-gray-800/20 px-5 py-3 flex items-center justify-between gap-2 text-gray-400 border-t border-gray-700/50">
                <div className="flex items-center gap-2">
                    <AiToolsButton
                        content={entry.content}
                        entryId={entry.id}
                        authorId={entry.authorId}
                        contextType="whisper"
                        onAnalysisComplete={(toolType, responseData) => onAiAction(toolType, { ...entry, ...responseData })}
                    />
                    <HoverTooltip text={t('like')}>
                        <button onClick={() => handleReaction('like')} className={`p-2 rounded-full transition-colors ${userReaction === 'like' ? 'text-green-400 bg-green-500/20' : 'hover:bg-gray-700/50'}`}><LucideIcons.ThumbsUp size={20} /></button>
                    </HoverTooltip>
                    <HoverTooltip text={t('dislike')}>
                        <button onClick={() => handleReaction('dislike')} className={`p-2 rounded-full transition-colors ${userReaction === 'dislike' ? 'text-red-400 bg-red-500/20' : 'hover:bg-gray-700/50'}`}><LucideIcons.ThumbsDown size={20} /></button>
                    </HoverTooltip>
                    <HoverTooltip text={t('amplify')}>
                        <button onClick={handleAmplify} disabled={isSelf} className="p-2 rounded-full hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><LucideIcons.Flame size={20} /></button>
                    </HoverTooltip>
                </div>
                <div className="flex items-center gap-4">
                    <HoverTooltip text={t('comments')}>
                        <button onClick={() => onComment(entry)} className="p-2 rounded-full hover:bg-gray-700/50 transition-colors flex items-center gap-1">
                            <LucideIcons.MessageSquare size={20} />
                            <span>{entry.commentsCount || 0}</span>
                        </button>
                    </HoverTooltip>
                    {isSelf && (
                        <>
                            <HoverTooltip text={t('userProfile.myWhispers.pin')}>
                                <button onClick={() => onPin(entry)} className="p-2 rounded-full hover:bg-gray-700/50 transition-colors"><LucideIcons.Pin size={20} /></button>
                            </HoverTooltip>
                            <HoverTooltip text={t('userProfile.myWhispers.spotlight')}>
                                <button onClick={() => showConfirmation({ message: t('userProfile.myWhispers.spotlightConfirmation'), onConfirm: () => onSpotlight(entry.id) })} className="p-2 rounded-full hover:bg-gray-700/50 transition-colors disabled:opacity-50" disabled={!canSpotlight}><LucideIcons.Star size={20} /></button>
                            </HoverTooltip>
                        </>
                    )}
                    {(isSelf || currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'owner') && (
                        <HoverTooltip text={t('delete')}>
                            <button onClick={() => showConfirmation({ message: t('reelPost.deleteConfirmation'), onConfirm: handleDelete })} className="p-2 rounded-full hover:bg-red-500/20 text-red-400 transition-colors">
                                <LucideIcons.Trash2 size={20} />
                            </button>
                        </HoverTooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WhisperCard;